import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase';
import {
  deriveJobFinancialState,
  jobFinancialBadgeClass,
  formatMoney,
} from '../../../../lib/invoiceFinancials';
import { reasonLabel } from '../../../../lib/billingIntent';

/**
 * Phase 2B — read-only financial detail for a completed job inside the
 * WorkOrderSheet. Derived from authoritative invoice records (nothing is
 * written to the job). Tenant-scoped to companies/{companyId}/invoices.
 *
 * Primary association is invoice.taskId === task.id (aggregates deposit +
 * final + any task-linked artifacts). A quoteId fallback is used ONLY for
 * legacy invoices that lack a taskId — it never attaches a recurring visit's
 * taskId-stamped invoice to a sibling visit.
 */
const JobFinancialSection = ({ companyId, task, leadId }) => {
  const [invoices, setInvoices] = useState([]);
  const taskId = task?.id;
  const quoteId = task?.quoteId;

  useEffect(() => {
    if (!companyId || !taskId) return undefined;
    const invoicesCol = collection(db, 'companies', companyId, 'invoices');
    const unsub = onSnapshot(
      query(invoicesCol, where('taskId', '==', taskId)),
      async (snap) => {
        const byTask = snap.docs.map((d) => ({ invoiceId: d.id, ...(d.data() || {}) }));
        if (byTask.length > 0 || !quoteId) {
          setInvoices(byTask);
          return;
        }
        // Legacy fallback: quote-level invoices with NO taskId only.
        try {
          const qs = await getDocs(query(invoicesCol, where('quoteId', '==', quoteId)));
          const legacy = qs.docs
            .map((d) => ({ invoiceId: d.id, ...(d.data() || {}) }))
            .filter((inv) => !inv.taskId);
          setInvoices(legacy);
        } catch {
          setInvoices([]);
        }
      },
      (err) => console.warn('[JobFinancialSection] listener error:', err),
    );
    return () => unsub();
  }, [companyId, taskId, quoteId]);

  const fin = deriveJobFinancialState({ task, invoices });

  const Row = ({ label, value }) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2" data-testid="work-order-financial">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Financial Status</span>
        <span
          data-testid="work-order-financial-badge"
          className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${jobFinancialBadgeClass(fin.state)}`}
        >
          {fin.label}
        </span>
      </div>

      {fin.invoiceCount > 0 && (
        <div className="space-y-1 pt-1">
          <Row label="Billed" value={formatMoney(fin.amountBilled)} />
          <Row label="Paid" value={formatMoney(fin.amountPaid)} />
          <Row label="Outstanding" value={formatMoney(fin.amountOutstanding)} />
          {fin.refundedAmount > 0 && <Row label="Refunded" value={formatMoney(fin.refundedAmount)} />}
        </div>
      )}

      {fin.state === 'unbilled' && (
        <p className="text-xs text-gray-400">No invoice is associated with this job.</p>
      )}
      {fin.state === 'covered' && (
        <p className="text-xs text-gray-400">This visit was covered by a prepaid package.</p>
      )}
      {fin.state === 'no_charge' && (
        <p className="text-xs text-gray-500" data-testid="work-order-no-charge-reason">
          Marked No Charge{fin.nonBillableReason ? ` · ${reasonLabel(fin.nonBillableReason)}` : ''} — no invoice was generated.
        </p>
      )}
      {fin.state === 'billing_attention' && (
        <p className="text-xs text-orange-600" data-testid="work-order-billing-attention">
          This job is billable but has no invoice yet. Create or send an invoice from the quote or Payments.
        </p>
      )}

      {leadId && fin.invoiceCount > 0 && (
        <Link
          to={`/clients/${leadId}?tab=payments`}
          className="inline-block pt-1 text-xs font-medium text-[var(--cb-orange)] hover:underline"
          data-testid="work-order-view-payments"
        >
          View in Payments →
        </Link>
      )}
    </div>
  );
};

export default JobFinancialSection;
